import { useState, useEffect, useCallback } from 'react';
import { httpClient } from '@/utils/httpClient';
import type { PingResult } from '../types';

export interface KycServer {
  id: string;
  name: string;
  url: string;
  port: number;
  apiKey: string;
  status: string;
  isForcedOffline: boolean;
}

interface UseKycServersReturn {
  servers: KycServer[];
  loading: boolean;
  pingResults: Record<string, PingResult>;
  pingServer: (server: KycServer) => Promise<void>;
  pingingId: string | null;
  refresh: () => void;
}

export const useKycServers = (): UseKycServersReturn => {
  const [servers, setServers] = useState<KycServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [pingResults, setPingResults] = useState<Record<string, PingResult>>({});
  const [pingingId, setPingingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await httpClient.get<any>('/admin/server/GetServers?draw=1&length=100&start=0&search=&column=&orderBy=asc');
      const data = res.data?.data || [];
      setServers(data);
    } catch { /* empty */ }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const pingServer = useCallback(async (server: KycServer) => {
    setPingingId(server.id);
    try {
      // Ping via backend proxy — avoids CORS issues
      const res = await httpClient.get<PingResult>(`/admin/server/Ping/${server.id}`);
      setPingResults(prev => ({ ...prev, [server.id]: res.data }));
    } catch (e: any) {
      setPingResults(prev => ({
        ...prev,
        [server.id]: {
          serverId: server.id as any,
          latencyMs: null,
          status: 'unreachable',
          error: e.message || 'Ping failed',
          checkedAt: new Date().toISOString(),
        },
      }));
    } finally {
      setPingingId(null);
    }
  }, []);

  return { servers, loading, pingResults, pingServer, pingingId, refresh };
};
